import React, { useMemo } from 'react'
import { buildSearchParamsFromFilters } from 'lib/search/searchUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import { EmptyArray, take, without } from 'lib/array/arrayUtils'
import TopPicksForMeCarouselOffer from '../TopPicksForMe/TopPicksForMeCarouselOffer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TopPicksForMeOfferSkeleton from '../TopPicksForMe/TopPicksForMeOfferSkeleton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

interface Props {
  filters?: App.OfferListFilters
  offerIds: Array<string>
  excludedOfferIds?: Array<string>
  heading: React.ReactNode
  subheading?: string
  trackingPageSource: string
  trackingUiTouchPoint: string
  fetching?: boolean
}

const Section = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
`

const OfferGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: ${rem(20)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

function ExpiredDealsHeroOffers({
  filters,
  offerIds,
  excludedOfferIds = EmptyArray,
  heading,
  subheading,
  trackingPageSource,
  trackingUiTouchPoint,
  fetching,
}: Props) {
  const finalOfferIds = useMemo(() => without(offerIds, ...excludedOfferIds), [excludedOfferIds, offerIds])
  const tracking = useOfferListTracking(
    getListName('LERE', trackingPageSource, trackingUiTouchPoint),
    { key: JSON.stringify(finalOfferIds) },
  )

  if (!finalOfferIds.length && !fetching) { return null }

  return (
    <Section>
      <VerticalSpacer gap={20}>
        <VerticalSpacer gap={4}>
          <Heading lineClamp={1} variant="heading2">{heading}</Heading>
          {!!subheading && <BodyText variant="medium" colour="neutral-two">{subheading}</BodyText>}
        </VerticalSpacer>
        <OfferGrid>
          {fetching && <>
            <TopPicksForMeOfferSkeleton />
            <TopPicksForMeOfferSkeleton />
            <TopPicksForMeOfferSkeleton />
            <TopPicksForMeOfferSkeleton />
            <TopPicksForMeOfferSkeleton />
            <TopPicksForMeOfferSkeleton />
          </>}
          <OfferListEventsProvider tracking={tracking}>
            {!fetching && take(finalOfferIds, 25).map((id, idx) => {
              const position = idx + 1
              const linkQuery = buildSearchParamsFromFilters(filters)

              return <TopPicksForMeCarouselOffer
                key={id}
                offerId={id}
                position={position}
                linkQuery={linkQuery}
                preload={position <= 6}
              />
            })}
          </OfferListEventsProvider>
        </OfferGrid>
      </VerticalSpacer>
    </Section>
  )
}

export default ExpiredDealsHeroOffers
